if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      init() {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
              this.initSwatchHover();
            }

            if (recommendations.querySelector('.product-card')) {
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else {
              if (recommendations.classList.contains('complementary-products')) {
                if (this.dataset.rebuyFallback !== 'false') {
                  const productId = this.dataset.productId || window.meta?.product?.id;
                  if (productId) {
                    this.fetchRebuyFallback(productId);
                  } else {
                    this.closest('.product__block')?.remove();
                  }
                } else {
                  this.closest('.product__block')?.remove();
                }
              } else {
                this.closest('.shopify-section').remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      fetchRebuyFallback(productId) {
        const REBUY_KEY = '25e396223fc010d4f8f4a371dfb83ec008548c04';

        fetch(
          `https://rebuyengine.com/api/v1/products/recommended?key=${REBUY_KEY}&shopify_product_ids=${productId}`
        )
          .then((r) => r.json())
          .then((data) => {
            const rebuyProducts = data?.data || data?.products || [];
            const limit = parseInt(this.dataset.limit, 10) || 4;
            const sliced = rebuyProducts.slice(0, limit);
            const ids = sliced.map((p) => p.id).filter(Boolean);

            if (!ids.length) {
              this.closest('.product__block')?.remove();
              return;
            }

            const query = ids.map((id) => `id:${id}`).join(' OR ');
            const sectionUrl = `/search?type=product&q=${encodeURIComponent(query)}&limit=${limit}&section_id=rebuy-complementary`;

            fetch(sectionUrl)
              .then((r) => r.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const results = doc.querySelector('.rebuy-complementary-results');

                if (!results || !results.querySelector('.product-card')) {
                  this.closest('.product__block')?.remove();
                  return;
                }

                this.renderRebuySection(results, ids);
                this.dispatchEvent(new CustomEvent('recommendations:loaded'));
              })
              .catch(() => {
                this.closest('.product__block')?.remove();
              });
          })
          .catch(() => {
            this.closest('.product__block')?.remove();
          });
      }

      renderRebuySection(resultsNode, ids) {
        // Rebuy-only: enable single-variant cards (the shared card markup keeps the
        // ATC button disabled waiting for a variant pick that never comes when the
        // product has no real variants). Multi-variant cards are left untouched.
        this.enableSingleVariantCards(resultsNode);

        // If filtering left no slides, drop the whole block.
        if (!resultsNode.querySelector('.swiper-slide')) {
          this.closest('.product__block')?.remove();
          return;
        }

        // Reorder slides to match Rebuy recommendation order using data-product-id
        if (ids && ids.length > 1) {
          const wrapper = resultsNode.querySelector('.swiper-wrapper');
          if (wrapper) {
            const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
            ids
              .map((id) => slides.find((s) => s.dataset.productId === String(id)))
              .filter(Boolean)
              .forEach((slide) => wrapper.appendChild(slide));
          }
        }

        const heading = this.dataset.rebuyHeading || this.dataset.heading;
        if (heading) {
          const container = resultsNode.querySelector('.complementary-products__container');
          if (container) {
            const h2 = document.createElement('h2');
            h2.className = 'h4 complementary-products__heading';
            h2.textContent = heading;
            container.insertBefore(h2, container.firstChild);
          }
        }

        this.innerHTML = resultsNode.innerHTML;

        const imageRatio = this.dataset.imageRatio;
        if (imageRatio && imageRatio !== 'adapt') {
          this.querySelectorAll('.product-card__image').forEach((wrapper) => {
            wrapper.style.setProperty('--aspect-ratio', imageRatio);
          });
        }
      }

      enableSingleVariantCards(resultsNode) {
        resultsNode.querySelectorAll('.swiper-slide').forEach((slide) => {
          const card = slide.querySelector('.product-card');
          if (!card) return;

          // A variant picker is only rendered for products with > 1 variant.
          // Its presence means the existing JS (product-card-variants.js) handles
          // enabling the button — leave those cards alone.
          if (card.querySelector('product-card-variants')) return;

          const available = slide.dataset.variantAvailable === 'true';
          const variantId = slide.dataset.variantId;
          const addToCartLabel = slide.dataset.addToCartLabel;

          // Single-variant + sold out (or missing id) → remove the slide entirely.
          if (!available || !variantId) {
            slide.remove();
            return;
          }

          const input = card.querySelector('.product-variant-id-complementary');
          const button = card.querySelector('.product-card__atc');
          const buttonText = card.querySelector('.product-card__atc-text');

          if (input) {
            input.value = variantId;
            input.removeAttribute('disabled');
          }
          if (button) {
            button.removeAttribute('disabled');
          }
          if (buttonText && addToCartLabel) {
            buttonText.textContent = addToCartLabel;
          }
        });
      }

      initSwatchHover() {
        if (typeof window.initProductSwatchHover === 'function') {
          window.initProductSwatchHover(this);
        }
      }
    }
  );
}
